import { FC, useEffect, useMemo, useState } from "react";
import { RotatingLines } from "react-loader-spinner";
import { DDGColors } from "../../constants/colors";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  MinusCircle,
  Share,
  Star,
} from "lucide-react";
import { shareOnMobile } from "react-mobile-share";
import { AppState } from "../../state";
import { useDispatch, useSelector } from "react-redux";
import {
  useSwitchPage,
  useUpdateCart,
  useWellnessClasses,
} from "../../state/features/applicationSlice/hooks";
import {
  ApplicationPages,
  setActiveClass,
  setCart,
} from "../../state/features/applicationSlice";
import Image from "../../components/Image";
import {
  convertDateToString,
  formatAddress,
  formatNumberScale,
  getInitials,
  getProfileImage,
  getTimestampDate,
} from "../../functions";
import {
  WellnessClass,
  WellnessClassHour,
} from "../../types/HealthInstitution";
import { Review } from "../../types/Consultation";
import Mapscreen from "../../components/Mapscreen";
import useCopy from "../../config/useCopy";
import { Hospital, PhoneIcon, UserCircleIcon, UsersIcon } from "lucide-react";
import { getFacilityReviews, getRatings } from "../../functions/firebase";

interface homeProps {
  isPopUp: boolean;
}

const Home: FC<homeProps> = ({ isPopUp }) => {
  const {
    activeFacility: hospital,
    institutionServices,
    institutionClasses,
    cart,
  } = useSelector<AppState, AppState["application"]>(
    (state) => state.application
  );
  const [toggleReviews, setToggleReviews] = useState(false);
  const router = useSwitchPage();
  const updateCart = useUpdateCart();
  const dispatch = useDispatch();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [hospitalReviews, setHospitalReviews] = useState<Review[]>([]);
  const [hospitalRating, setHospitalRating] = useState<{
    overallRating: number;
    count: number;
  }>({
    overallRating: 0,
    count: 0,
  });
  const [activeCategory, setActiveCategory] = useState("");
  const hospitalImages = useMemo(() => {
    return hospital
      ? [
          hospital.details.coverImage,
          ...(hospital.details.gallery ? hospital.details.gallery : []),
        ]
      : [];
  }, [hospital]);
  const { handleCopy } = useCopy();

  const formatClassDays = (classHours: WellnessClassHour[]) => {
    const dayAbbreviations = classHours
      .map((hour) => hour.day.toLowerCase()) // ensure all lowercase
      .filter((day, index, arr) => arr.indexOf(day) === index) // remove duplicates
      .map((day) => day.charAt(0).toUpperCase() + day.slice(1, 3)); // "sunday" → "Sun"

    return dayAbbreviations.join(", ");
  };
  if (!hospital) {
    return <></>;
  }
  return (
    <main className="relative flex flex-col bg-white">
      <div className="flex-1">
        <div className="relative h-64">
          <div className="absolute z-10 flex flex-row items-center justify-between w-full px-4 top-4">
            <button
              onClick={() => {
                router(ApplicationPages.HOME);
              }}
              className="flex items-center justify-center w-10 h-10 bg-black/30 backdrop-blur-md rounded-full text-white hover:bg-black/50 transition-all duration-300 shadow-sm"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={() =>
                shareOnMobile(
                  {
                    text: `I just found my new wellness sanctuary at ${hospital.details.institutionName}. Rejuvenate your mind and body—you deserve this! Check it out`,
                    url: window.location.href,
                    title: `${hospital.details.institutionName} - DrDoGood`,
                  },
                  (message) => {
                    console.log("message sharing", message);
                    handleCopy(window.location.href);
                  }
                )
              }
              className="flex items-center justify-center w-10 h-10 bg-black/30 backdrop-blur-md rounded-full text-white hover:bg-black/50 transition-all duration-300 shadow-sm"
            >
              <Share size={20} />
            </button>
          </div>

          {/* Background image */}
          <div className="relative h-64 bg-gray-900 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10 pointer-events-none"></div>
            <Image
              className="object-cover w-full h-full transform transition-transform duration-1000 hover:scale-105"
              src={
                hospitalImages.length > 0
                  ? hospitalImages[activeSlideIndex] ?? ""
                  : hospital.details.coverImage ?? ""
              }
              alt={hospital.details.institutionName ?? ""}
              useBase={false}
            />
          </div>

          {/* Logo and title */}
          <div className="absolute left-0 flex items-center w-full px-5 bottom-8 z-20">
            <div className="mr-4">
              <div className="flex items-center justify-center w-14 h-14 overflow-hidden bg-white shadow-lg rounded-2xl border border-white/20">
                <div className="text-xs font-bold text-center text-red-500 w-full h-full">
                  <Image
                    useBase={false}
                    className="flex-1 w-full h-full object-cover p-1"
                    src={hospital.details.logoImage ?? ""}
                    alt={hospital.details.institutionName}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="flex flex-row items-center space-x-1.5 mb-1">
                <Star
                  size={14}
                  className="text-yellow-400 fill-current"
                />
                <p className="text-xs font-semibold text-yellow-400 tracking-wide">
                  {hospitalRating.overallRating.toFixed(1)}
                </p>
                <span className="text-white/60 text-xs tracking-wide mx-1">•</span>
                <p className="text-xs text-white/80 font-medium tracking-wide">
                  {hospitalRating.count} reviews
                </p>
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight drop-shadow-md">
                {hospital.details.institutionName ?? ""}
              </h1>
              <div className="text-sm font-medium text-white/90 drop-shadow-sm mt-0.5 flex items-center">
                {hospital.details.address.city},{" "}
                {hospital.details.address.country}
              </div>
            </div>
          </div>

          {/* Carousel indicators */}
          {hospitalImages?.length > 1 && (
            <div className="absolute flex justify-between w-full px-4 space-x-1 bottom-2">
              <div className="space-x-1">
                {hospitalImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveSlideIndex(index)}
                    className={`rounded-full h-2 w-2 self-center ${
                      activeSlideIndex === index
                        ? "bg-[#63BBB7]"
                        : "bg-[#63bbb77c]"
                    }`}
                  ></button>
                ))}
              </div>
              <div className="space-x-3">
                <button
                  disabled={activeSlideIndex === 0}
                  onClick={() => {
                    activeSlideIndex > 0 &&
                      setActiveSlideIndex(activeSlideIndex - 1);
                  }}
                  className={`rounded-full p-1 bg-white ${
                    activeSlideIndex === 0 ? "opacity-50 " : "bg-white"
                  }`}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  disabled={activeSlideIndex >= hospitalImages.length - 1}
                  onClick={() => {
                    setActiveSlideIndex(activeSlideIndex + 1);
                  }}
                  className={`rounded-full p-1 bg-white ${
                    activeSlideIndex >= hospitalImages.length - 1
                      ? "opacity-50"
                      : "bg-white"
                  }`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-4xl px-5 mx-auto mt-4">
          <div className="">
            <p className="pb-2 text-lg font-bold text-black">About us</p>
            <p className="text-base font-light text-gray900">
              {hospital.details.description}
            </p>
          </div>
          {/* {[...institutionDoctors].length > 0 && (
						<button
							onClick={() => {
								router(ApplicationPages.SPEAK_TO_DOCTOR)
							}}
							className='flex flex-row items-center w-full p-4 border-b border-gray-100 '
						>
							<div className='p-2 bg-[#F0FFFE] rounded-full'>
								<Image useBase src='/icons/heart.svg' alt='' />
							</div>
							<div className='flex-1 px-3 text-left'>
								<p className='font-medium text-[#101828] text-xs'>
									Speak to a doctor
								</p>
								<p className='text-[10px] text-[#667085]'>
									Connect with a Healthcare Professional Instantly for Your
									Health Concerns
								</p>
							</div>
							<button className='p-2 rounded-full'>
								<ChevronRight className='text-gray700' />
							</button>
						</button>
					)} */}
          {[...institutionClasses].length > 0 && (
            <div className="pt-10">
              <h2 className="pb-2 text-lg font-bold text-black">
                Classes We Offer
              </h2>
              <div className="flex w-full gap-4 pb-4 mx-auto overflow-x-auto">
                {[...institutionClasses].map((wellnessClass) => {
                  return (
                    <div
                      key={wellnessClass.id}
                      onClick={() => {
                        dispatch(setActiveClass(wellnessClass));
                        router(ApplicationPages.BOOKING_CLASS_START);
                      }}
                      className="relative w-[266px] shrink-0 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1 bg-white"
                    >
                      <div className="relative w-full">
                        <div
                          style={{
                            backgroundImage: `url(${
                              wellnessClass.image
                                ? wellnessClass.image
                                : hospital.details.coverImage
                            })`,
                            backgroundPosition: "center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                          }}
                          className="relative w-full h-[143px] rounded-lg overflow-hidden"
                        >
                          <div className="absolute flex items-center gap-3 left-4 bottom-4">
                            {[...(wellnessClass.gallery ?? [])]
                              .splice(0, 2)
                              .map((gal) => {
                                return (
                                  <div
                                    key={gal}
                                    className="w-8 h-8 rounded-lg"
                                    style={{
                                      backgroundImage: `url(${gal})`,
                                      backgroundPosition: "center",
                                      backgroundSize: "cover",
                                      backgroundRepeat: "no-repeat",
                                    }}
                                  ></div>
                                );
                              })}
                            {wellnessClass.gallery.length > 3 ? (
                              <div
                                style={{
                                  backgroundImage: `url(${wellnessClass.gallery[2]})`,
                                  backgroundPosition: "center",
                                  backgroundSize: "cover",
                                  backgroundRepeat: "no-repeat",
                                }}
                                className="relative w-8 h-8 rounded-lg"
                              >
                                <p className="absolute font-medium text-white -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">{`+${
                                  wellnessClass.gallery.length - 3
                                }`}</p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-black ">
                              {wellnessClass.title}
                            </p>
                            <div className="self-start px-3 py-1 rounded-xl bg-[#EDFBF8] text-[#034A47]">
                              <p className="text-[10px] font-bold">
                                {wellnessClass.category}
                              </p>
                            </div>
                          </div>
                          <div className=" pt-2 pb-3 border-b border-[#EEEEEE]">
                            <p className=" font-light text-sm line-clamp-3 text-[#3A3A3A]">
                              {wellnessClass.description}
                            </p>
                          </div>
                          <div className="flex items-center justify-between pt-3">
                            <div className="flex items-center gap-4">
                              <div className="flex flex-col gap-1">
                                <span className="text-xs text-[#55565B]">
                                  Base price
                                </span>
                                <span className="text-sm font-medium text-[#000000]">
                                  {`${wellnessClass.isFree ? 'FREE': `GH₵${wellnessClass.price}`}`}
                                </span>
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-xs text-[#55565B]">
                                  Class days
                                </span>
                                <span className="text-sm font-medium text-[#000000]">
                                  {wellnessClass.classHours.length > 0
                                    ? formatClassDays(wellnessClass.classHours)
                                    : "--"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* <div className='relative flex p-2 space-x-2'>
													{wellnessClass.gallery.map((gal) => {
														return (
															<div key={gal} className='relative w-12 h-10 '>
																<img
																	className='object-cover w-20 h-8 rounded-lg'
																	alt={wellnessClass.title}
																	src={gal}
																/>
															</div>
														)
													})}
												</div>
												<div className='flex flex-col px-4 pt-2 pb-4'>
													<div className='flex items-center justify-between'>
														<div className='self-start px-3 py-1 rounded bg-primaryLight text-primary'>
															<p className='text-[10px] font-semibold'>
																{wellnessClass.category}
															</p>
														</div>
														<div className='flex items-center px-2 py-1 rounded-full border-0.5 space-x-2 border border-gray-400'>
															<Users size={12} />{' '}
															<p className='text-xs font-semibold'>
																{wellnessClass.capacity}
															</p>
														</div>
													</div>
													<div className='pt-4 space-y-3'>
														<p className='text-sm font-bold'>
															{wellnessClass.title}
														</p>
														<p className='text-xs line-clamp-3'>
															{wellnessClass.description}
														</p>
														<div>
															<p className='pb-1 text-xs font-bold text-gray-500'>
																Class hours
															</p>
															{wellnessClass.classHours.map((classHour) => {
																return (
																	<div
																		key={classHour.id}
																		className='flex items-center justify-between text-sm'
																	>
																		<p className='font-semibold capitalize'>
																			{classHour.day}s:{' '}
																		</p>{' '}
																		<p className='text-xs'>
																			{classHour.interval.from} -{' '}
																			{classHour.interval.to}
																		</p>
																	</div>
																)
															})}
														</div>
														<div className='flex items-end justify-between'>
															<div>
																<p className='pb-1 text-xs font-bold text-gray-500'>
																	Prices
																</p>
																<p className='text-xs'>
																	Original -{' '}
																	<span className='font-bold'>
																		GH₵{wellnessClass.price}
																	</span>
																</p>
																{wellnessClass.memberAccess && (
																	<p className='text-xs'>
																		Member Price - {wellnessClass.memberAccess}
																	</p>
																)}
															</div>
															<div>
																<button className='px-3 py-1 text-xs font-semibold border rounded-full'>
																	Book Now
																</button>
															</div>
														</div>
													</div>
												</div> */}
                      </div>
                      <div className="absolute flex items-center gap-1 text-white top-4 right-4">
                        <UsersIcon className="w-4 h-4" />
                        <p className="font-semibold ">
                          {wellnessClass.capacity}
                        </p>
                      </div>
                    </div>
                    // <div className='w-[266px] rounded-lg'>
                    // 	<div>

                    // 	</div>

                    // </div>
                  );
                })}
              </div>
            </div>
          )}
          {[...institutionServices].length > 0 && (
            <div className="pt-10">
              <h2 className="pb-2 text-lg font-bold text-black">
                Our Services
              </h2>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  className={`py-2 px-5 text-sm font-medium rounded-full transition-all duration-300 ${
                    activeCategory === ""
                      ? "bg-[#034A47] text-white shadow-md transform scale-105"
                      : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                  } `}
                  onClick={() => {
                    setActiveCategory("");
                  }}
                >
                  All
                </button>
                {[...(hospital?.serviceCategories ?? [])].map(
                  (category, index) => {
                    return (
                      <button
                        key={index}
                        className={`py-2 px-5 text-sm font-medium rounded-full transition-all duration-300 ${
                          category.id === activeCategory
                            ? "bg-[#034A47] text-white shadow-md transform scale-105"
                            : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                        }`}
                        onClick={() => {
                          setActiveCategory(category.id);
                        }}
                      >
                        {category.name}
                      </button>
                    );
                  }
                )}
              </div>
              {/* <div className='pt-4'>
								<div className='relative'>
									<div
										className={`absolute bottom-0 w-full h-1 bg-gray-300 `}
									/>
									<div
										className='flex flex-row items-center px-4 space-x-3 overflow-x-scroll '
										style={{
											scrollbarWidth: 'none',
										}}
									>
										<button
											onClick={() => setActiveCategory('')}
											className='relative w-auto h-12 px-4 pb-2'
										>
											<p
												className={`${
													!activeCategory ? 'text-primary' : 'text-dark'
												} text-[10px] font-bold uppercase`}
											>
												All
											</p>
											<div
												className={`absolute left-0 bottom-0 w-full px-4 ${
													!activeCategory ? 'bg-primary' : ''
												}  h-1 rounded-full `}
											/>
										</button>
										{[...(hospital?.serviceCategories ?? [])].map(
											(category) => {
												return (
													<button
														onClick={() => setActiveCategory(category.id)}
														className='relative h-12 px-4 pb-2 w-72'
													>
														<p
															style={{ whiteSpace: 'nowrap' }}
															className={`${
																activeCategory === category.id
																	? 'text-primary'
																	: 'text-dark'
															} text-[8px] font-bold uppercase white-space: nowrap;`}
														>
															{category.name}
														</p>

														<div
															className={`left-0 ${
																activeCategory === category.id
																	? 'bg-primary'
																	: ''
															} w-full self-end h-1 absolute -bottom-0 rounded-full`}
														/>
													</button>
												)
											}
										)}
									</div>
								</div>*/}

              {hospital?.details.institutionType && (
                <div className="grid w-full grid-cols-2 gap-5 pt-10 sm:flex sm:flex-wrap">
                  {[...(institutionServices ? institutionServices : [])]
                    .filter(
                      (item) =>
                        (!activeCategory || item.category === activeCategory) &&
                        item.cost
                    )
                    .sort((a, b) => a.title.localeCompare(b.title))
                    .map((service, index) => {
                      const isChecked = cart?.some(
                        (item) => service.id === item.id
                      );

                      return (
                        <button
                          key={index}
                          onClick={() => {
                            updateCart([service]);
                          }}
                          // className={`relative flex flex-col ${
                          // 	isPopUp ? 'w-1/2' : 'w-[166px]'
                          // } p-2 overflow-hidden text-left h-52`}
                          className="sm:w-[197px] w-full flex flex-col rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1 bg-white text-left"
                        >
                          <div
                            style={{
                              backgroundImage: `url(${
                                service.image
                                  ? service.image
                                  : hospital.details.coverImage
                              })`,
                              backgroundPosition: "center",
                              backgroundSize: "cover",
                              backgroundRepeat: "no-repeat",
                            }}
                            className="relative h-[135px] w-full rounded-xl overflow-hidden"
                          ></div>
                          <div className="flex flex-col justify-between gap-3 px-3 py-6">
                            <div className="text-black ">
                              <p className="text-sm font-medium">
                                {service.title}
                              </p>
                            </div>
                            <div className="">
                              <p className="text-sm text-[#034A47] font-semibold">
                                GH₵{Number(service.cost) + 15}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
              {/* </div>  */}
            </div>
          )}
          {/* <div className='flex-row items-center p-4 '>
						{hospital?.details?.twitter && (
							<button
								onClick={() => {
									window.open(`https://x.com/${hospital.details.twitter}`)
								}}
								className='mx-1'
							>
								<Twitter size={24} />
							</button>
						)}
						{hospital?.details?.instagram && (
							<button
								onClick={() => {
									window.open(
										`https://instagram.com/${hospital.details.instagram}`,
										'_blank'
									)
								}}
								className='mx-1'
							>
								<Instagram size={24} />
							</button>
						)}

						{hospital?.details?.facebook && (
							<button
								onClick={() => {
									window.open(
										`https://facebook.com/${hospital.details.facebook}`,
										'_blank'
									)
								}}
								className='mx-1'
							>
								<Facebook size={24} />
							</button>
						)}
						{hospital?.details?.tiktok && (
							<button
								onClick={() => {
									window.open(
										`https://tiktok.com/@${hospital.details.tiktok}`,
										'_blank'
									)
								}}
								className='mx-1'
							>
								<Instagram size={24} />
							</button>
						)}
						{hospital?.details?.website && (
							<button
								onClick={() => {
									window.open(`https://${hospital.details.website}`, '_blank')
								}}
								className='mx-1'
							>
								<Globe size={28} />{' '}
							</button>
						)}
					</div> */}
          {hospital.details.institutionType === "hospital" && (
            <div className="pt-10 ">
              <h2 className="pb-2 text-lg font-bold text-black">Facilities</h2>
              <div className="flex flex-wrap items-center gap-3 ">
                {hospital.practiceSpecialties.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="p-3 rounded-2xl shadow-sm border border-gray-100 bg-white hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex items-center justify-center w-10 h-10 rounded-xl ${
                            index % 2 === 0 ? "bg-[#CDF6E2]/50 text-[#034A47]" : "bg-[#F4EEBE]/50 text-[#8B8000]"
                          }`}
                        >
                          <Hospital className="w-5 h-5" />
                        </div>
                        <p className="font-medium text-black ">
                          {typeof item === "string" ? item : item.name}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="pt-10">
            <h2 className="pb-2 text-lg font-bold text-black">
              Comments and Reviews{" "}
              <span className="text-[#9D9D9D]">{`(${
                hospitalReviews.filter((review) => review.feedback).length
              })`}</span>
            </h2>
            <div
              className={`flex flex-col gap-6 rounded-2xl bg-white shadow-sm border border-gray-100 ${
                hospitalReviews.filter((review) => review.feedback).length > 0
                  ? "p-5"
                  : ""
              }`}
            >
              {hospitalReviews
                .filter((review) => review.feedback)
                .splice(0, 3)
                .map((review, index) => {
                  return (
                    <div key={index} className="flex flex-col space-y-2 pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-500">
                            <UserCircleIcon className="w-6 h-6" />
                          </div>
                          <p className="text-sm text-black">
                            {review.patientData.name}
                          </p>
                        </div>
                        <p className="text-sm text-black">
                          {convertDateToString(
                            getTimestampDate(review.createdAt)
                          )}
                        </p>
                      </div>
                      <Star
                      // readonly
                      // type="star"
                      // ratingCount={5}
                      // startingValue={review.rating}
                      // imageSize={12}
                      />
                      <p className="text-[#3A3A3A] text-sm">
                        {review.feedback}
                      </p>
                    </div>
                  );
                })}
              {hospitalReviews.filter((review) => review.feedback).length > 3 &&
                hospitalReviews
                  .filter((review) => review.feedback)
                  .splice(3)
                  .map((review, index) => {
                    return (
                      <div
                        key={index}
                        className={`flex-col space-y-2 pb-4 border-b border-gray-50 last:border-0 last:pb-0 ${
                          toggleReviews ? "flex" : "hidden"
                        } `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-500">
                              <UserCircleIcon className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-black">
                              {review.patientData.name}
                            </p>
                          </div>
                          <p className="text-sm text-black">
                            {convertDateToString(
                              getTimestampDate(review.createdAt)
                            )}
                          </p>
                        </div>
                        <Star
                        // readonly
                        // type="star"
                        // ratingCount={5}
                        // startingValue={review.rating}
                        // imageSize={12}
                        />
                        <p className="text-[#3A3A3A] text-sm">
                          {review.feedback}
                        </p>
                      </div>
                    );
                  })}
              {hospitalReviews.filter((review) => review.feedback).length >
                3 && (
                <button
                  onClick={() => setToggleReviews(!toggleReviews)}
                  className=" flex gap-1 items-center justify-center py-4 text-sm text-[#067470]"
                >
                  {toggleReviews ? "Show less" : "Show more"}{" "}
                  {toggleReviews ? (
                    <ChevronUp size={16} className="mt-1 " />
                  ) : (
                    <ChevronDown size={16} className="mt-1 " />
                  )}
                </button>
              )}
            </div>

            {/* {hospitalReviews
							.filter((review) => review.feedback)
							.map((review, index) => {
								return (
									<div key={index} className='items-start my-2 space-y-1'>
										<Star
										// readonly
										// type="star"
										// ratingCount={5}
										// startingValue={review.rating}
										// imageSize={12}
										/>
										<p className='font-semibold text-dark'>{review.feedback}</p>
										<div className='flex flex-row items-center space-x-2'>
											<p className='text-[10px] text-gray-500'>
												{convertDateToString(
													getTimestampDate(review.createdAt)
												)}
											</p>
											<p className='text-gray-500'>•</p>
											{review.patientData?.name && (
												<div className='flex flex-row items-center space-x-2'>
													<p className='text-[10px] text-gray-500 font-bold'>
														{review.patientData?.name.split(' ')[0]}{' '}
														<p className='uppercase'>
															{review.patientData?.name
																.split(' ')[1]
																?.slice(0, 1)}
															.
														</p>
													</p>
													<p className='text-gray-500'>•</p>
													<p className='text-[10px] text-gray-500'>
														Verified Patient
													</p>
												</div>
											)}
										</div>
									</div>
								)
							})} */}
          </div>
          <div className="pt-10">
            <h2 className="pb-2 text-lg font-bold text-black">Opening Hours</h2>
            <div className="rounded-xl overflow-hidden shadow-[2.5px_2.5px_20px_#46464625]">
              <div className="flex items-center justify-between px-6 py-4 text-sm font-bold bg-[#E1EAE9]  text-[#3A3A3A]">
                <span>Days of the week</span>
                <span>Class Duration</span>
              </div>
              <div className="flex flex-col gap-4 p-6">
                {hospital?.default_opening_hours.map((availability, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm font-bold text-black">
                        {availability.day}
                      </span>
                      <span className="text-[#8C8C8C] text-sm">
                        {availability.interval.from} -{" "}
                        {availability.interval.to}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* <div className='rounded-t-full h-96'>
						<div className='p-4'>
							<p className='pb-1 text-sm font-bold capitalize'>Location</p>
							<p className='text-xs text-slate-500'>
								{hospital.details.address
									? formatAddress(hospital.details.address)
									: ''}
							</p>
							<button
								onClick={() => {
									const latLng = `${hospital.details.address.lat},${hospital.details.address.lng}`
									const url = `https://www.google.com/maps/dir/?api=1&destination=${latLng}&travelmode=driving`
									window.open(url, '_blank')
								}}
								className='flex flex-row items-center pt-2 space-x-2'
							>
								<Navigation name='directions' color={'#64748b'} size={14} />
								<p className='text-xs text-slate-500'>Get directions</p>
							</button>
						</div>
						<Mapscreen origin={hospital.details.address} />
					</div> */}
          <div className="pt-10">
            <h2 className="pb-2 text-lg font-bold text-black">
              Facility Location
            </h2>
            <div className="min-h-[282px] p-4 sm:p-5 rounded-xl overflow-hidden shadow-[2.5px_2.5px_20px_#46464625]">
              <div className="h-[144px] sm:h-1/2 rounded-xl overflow-hidden">
                <Mapscreen origin={hospital.details.address} />
              </div>
              <div className="flex flex-col pt-3 pb-4 ">
                <span className="text-[#8C8C8C] text-sm font-normal">
                  Business Address
                </span>
                <span className="text-sm font-medium text-black">
                  {hospital.details.address
                    ? formatAddress(hospital.details.address)
                    : ""}
                </span>
              </div>
              <div className="flex items-center w-full sm:justify-start">
                <button
                  onClick={() => {
                    const latLng = `${hospital.details.address.lat},${hospital.details.address.lng}`;
                    const url = `https://www.google.com/maps/dir/?api=1&destination=${latLng}&travelmode=driving`;
                    window.open(url, "_blank");
                  }}
                  className="bg-[#034A47] text-white w-full md:w-fit md:px-5 py-3 rounded-xl"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-20 p-5 flex items-center justify-between rounded-xl overflow-hidden shadow-[2.5px_2.5px_20px_#46464625]">
            <div className="flex flex-col">
              <span className="text-lg font-medium text-black">
                Contact Support
              </span>
              <span className="text-[#8C8C8C] text-sm">
                Our team is ready to help anytime
              </span>
            </div>
            <a
              href={`tel:${hospital.details.contactPhone}`}
              className="px-4 py-2 flex items-center justify-center rounded-3xl bg-[#E1EAE9]"
            >
              <PhoneIcon className="h-6 w-6 text-[#292D32]" />
            </a>
          </div>
        </div>
      </div>
      {cart?.length > 0 && (
        <button
          onClick={() => {
            router(ApplicationPages.BOOKING_SERVICE_START);
          }}
          className="sticky z-40 self-stretch w-full h-10 px-4 shadow-3xl bottom-5 "
        >
          <div className="relative flex items-center justify-between h-full flex-1 bg-[#1F2223] rounded-lg py-3 px-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-4 h-4 bg-white rounded-full -top-2 right-1">
                <p className="text-[10px] text-dark">{cart?.length}</p>
              </div>
              <p className="text-xs font-semibold text-white">Checkout</p>
            </div>
            <div className="flex space-x-2">
              <p className="text-xs font-semibold text-white">
                {formatNumberScale(
                  [...cart].reduce(
                    (acc, val) => acc + (Number(val.price) + 15),
                    0
                  ),
                  true
                )}
              </p>
              <button
                onClick={() => {
                  dispatch(setCart([]));
                }}
              >
                <MinusCircle size={14} color={DDGColors.lightBlood} />
              </button>
            </div>
          </div>
        </button>
      )}
    </main>
  );
};

export default Home;

// const specialtyIcon = (specialty) => {
//   switch (specialty) {
//     case "Dentistry":
//       return (
//         <FontAwesome5 color={DDGColors.primary} name="teeth" size={18} />
//       );
//     case "Neurology":
//       return (
//         <FontAwesome5 color={DDGColors.primary} name="brain" size={18} />
//       );
//     case "Optometry/Ophthalmology":
//       return <Eye color={DDGColors.primary} name="eye" size={18} />;
//     case "Pediatric":
//       return <Babuy color={DDGColors.primary} name="baby" size={18} />;
//     case "Obstetrics/Gynecology":
//       return (
//         <MaterialCommunityIcons
//           color={DDGColors.primary}
//           name="mother-nurse"
//           size={18}
//         />
//       );
//     case "Primary Care":
//       return (
//         <MaterialCommunityIcons
//           color={DDGColors.primary}
//           name="mother-nurse"
//           size={18}
//         />
//       );
//     case "Cardiology":
//       return (
//         <FontAwesome5 color={DDGColors.primary} name="heart" size={18} />
//       );
//     default:
//       return (
//         <MaterialIcons
//           color={DDGColors.primary}
//           name="health-and-safety"
//           size={18}
//         />
//       );
//   }
// };
